import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';

import { JobSurge, paySurgeClient, surgeErrorMessage } from '../api';
import { surgeOffNote, surgeScopeNote } from '../format';
import useSurgeDraft from '../hooks/useSurgeDraft';

import ConfirmSurgeDialog from './ConfirmSurgeDialog';
import SurgeDialog from './SurgeDialog';
import SurgeTierEditor from './SurgeTierEditor';

type Props = {
  surge: JobSurge;
  onSaved: (surge: JobSurge) => void;
  onClose: () => void;
};

/**
 * Manage surge pay — one shift's own schedule (GRAVY-10443, AC 5 and 6; Figma
 * 38664:8957). Saves THIS job's copy only; the client default and every other
 * shift are untouched, which the footer note says and the server enforces.
 */
const ManageSurgePayModal = ({ surge, onSaved, onClose }: Props) => {
  const state = useSurgeDraft(
    { enabled: surge.enabled, tiers: surge.tiers },
    {
      maxCents: surge.max_cents,
      leadTimeOptions: surge.lead_time_options_minutes,
    },
  );
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      onSaved(await paySurgeClient.saveJobSurge(surge.job_id, state.schedule));
      onClose();
    } catch (e) {
      setConfirming(false);
      setError(
        surgeErrorMessage(e) ?? 'Could not save surge pay. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <SurgeDialog
        testId="manageSurgePay"
        title="Manage surge pay"
        onClose={onClose}
      >
        <SurgeTierEditor
          state={state}
          maxCents={surge.max_cents}
          leadTimeOptions={surge.lead_time_options_minutes}
          offDescription={surgeOffNote(surge.shifts_count)}
          settledLeadTimes={surge.tiers
            .filter((tier) => tier.state !== 'pending')
            .map((tier) => tier.lead_time_minutes)}
        />
        <p className="paysurge-info">
          <InformationCircleIcon aria-hidden="true" />
          {surgeScopeNote(surge.shifts_count)}
        </p>
        {error && (
          <p className="paysurge-formerror" role="alert">
            {error}
          </p>
        )}
        <div className="paysurge-actions">
          <button
            type="button"
            className="paysurge-btn is-primary is-wide"
            data-testid="manageSurgeSave"
            disabled={!state.dirty || !state.valid || saving}
            onClick={() => setConfirming(true)}
          >
            Save
          </button>
        </div>
      </SurgeDialog>
      {confirming && (
        <ConfirmSurgeDialog
          scope="shift"
          shiftsCount={surge.shifts_count}
          saving={saving}
          onCancel={() => setConfirming(false)}
          onConfirm={save}
        />
      )}
    </>
  );
};

export default ManageSurgePayModal;
